<style>
/* Customize NProgress Color */
#nprogress .bar {
  background: var(--theme-color-name) !important;
}
#nprogress .peg {
  box-shadow:
    0 0 10px var(--theme-color-name),
    0 0 5px var(--theme-color-name) !important;
}
#nprogress .spinner-icon {
  border-top-color: var(--theme-color-name) !important;
  border-left-color: var(--theme-color-name) !important;
}
</style>
<script setup lang="ts">
import '@unocss/reset/tailwind-compat.css'

if (import.meta.client) {
  console.log('5. [App.vue] setup start:', Date.now(), 'Diff:', Date.now() - (window as any).__START_TIME__ + 'ms')
}

// import { RouterView } from 'vue-router'
import { useHead } from '#imports'
import { ElConfigProvider } from 'element-plus'
import { nextTick, onMounted, onUnmounted, ref, computed, type Ref, provide } from 'vue'
import zh_CN from 'element-plus/es/locale/lang/zh-cn'
import zh_TW from 'element-plus/es/locale/lang/zh-tw'
import en_US from 'element-plus/es/locale/lang/en'
import { currentLang } from './composables/settings/storage'
import type { Language } from 'element-plus/es/locale'
// import VConsole from 'vconsole'
import { checkInMiniPrograme, auditMode } from './utils/util'
import { debounce } from 'perfect-debounce'
import { STORAGE_KEY_THEME, MINI_AUDIT_VERSION_CODE } from '@/constants'
import { getMiniProgramVersion } from '@/composables/home/home'
import { handleErr } from '@/composables/exception/handler'
import type { CommonResponse } from '@/apis/api'
import type { GetMiniProgramVersionResponse } from '@/composables/home/home'
import { CODE_OK } from '@/apis/code'
import { runLocalStorageMigrations } from '@/composables/storage/localStorageMigration'
import { useServicesStore } from '@/composables/memorize/store'
import { useRoute } from 'vue-router'
const route = useRoute()
console.log('5. [App.vue] setup start: Nuxt')

onMounted(async () => {
  if (import.meta.client) {
    console.log(
      '6. [App.vue] onMounted start:',
      Date.now(),
      'Diff:',
      Date.now() - (window as any).__START_TIME__ + 'ms'
    )
  }
  checkInMiniPrograme()

  // try {
  //   // Dynamic load vConsole (temporarily enabled for all environments)
  //   const { default: VConsole } = await import('vconsole')
  //   const vConsole = new VConsole()
  // } catch (err) {
  //   console.error('Failed to load vConsole:', err)
  // }

  // nextTick(() => {
  //   if (!isInWechat()) {
  //     vConsole?.destroy()
  //   }
  // })

  await nextTick()
  if (import.meta.client) {
    console.log(
      '7. [App.vue] onMounted nextTick (Render finished):',
      Date.now(),
      'Diff:',
      Date.now() - (window as any).__START_TIME__ + 'ms'
    )

    // Hide the loading animation as soon as the app is ready
    const loadingElement = document.getElementById('loading')
    if (loadingElement) {
      loadingElement.style.display = 'none'
    }
  }
})

onUnmounted(() => {})
useHead({
  script: [
    {
      innerHTML: `
;(function() {
  const html = document.documentElement
  
  const theme = {
    "--theme-color-name": "#fcd826",
    "--c-primary": "var(--rgb-primary)",
    "--c-primary-active": "#E9C81B",
    "--c-primary-light": "#FCF890",
    "--c-primary-fade": "#FFFED7",
    "--rgb-primary": "#fcd826",
    "--c-dark-primary": "rgb(var(--rgb-dark-primary))",
    "--c-dark-primary-active": "#e3b400",
    "--c-dark-primary-light": "#ffcd1380",
    "--c-dark-primary-fade": "#f9c8041a",
    "--rgb-dark-primary": "255, 200, 4"
  }

  // const savedThemeStr = localStorage.getItem('${STORAGE_KEY_THEME}')
  // Theme handling moved to Nuxt ColorMode or separate plugin using useCookie. 
  // For now, allow client side hydration to pick it up.
  const savedThemeStr = localStorage.getItem('${STORAGE_KEY_THEME}')
  
  const defaultThemeColors = {
    "--theme-main": "#fcd826",
    "--theme-dark": "#000",
    "--theme-light": "#fefefe",
    "--theme-light-80": "rgba(254, 254, 254, 0.8)",
    "--theme-light-0": "rgba(254, 254, 254, 0)",
    "--theme-gray": "#808080",
    "--theme-white": "#fff"
  }

  const savedTheme = (savedThemeStr != null && savedThemeStr != '') ? JSON.parse(savedThemeStr) : defaultThemeColors
  Object.entries(savedTheme || {}).forEach(([k, v]) => html.style.setProperty(k, v))

  Object.entries(theme).map(i => html.style.setProperty(i[0], i[1]))
})()`.trim()
    }
  ]
})

// element ui setting
const zIndex = ref(3000)
// 改为 computed 使 locale 响应 currentLang 变化
// 参考：https://element-plus.org/zh-CN/guide/i18n#configprovider
const locale = computed(() => {
  const lang = currentLang.value
  switch (lang) {
    case 'zh_CN':
      return zh_CN as Language
    case 'zh_HK':
      return zh_TW as Language // 繁体中文使用 zh-tw
    case 'en_US':
      return en_US as Language
    default:
      return zh_CN as Language
  }
})

let firstUrl: Ref<string> = ref('')
const versionCode = ref()

const runStorageCleanAsync = () => {
  const clean = () => {
    try {
      runLocalStorageMigrations()
      useServicesStore()
    } catch {}
  }
  const win = window as Window & {
    requestIdleCallback?: (callback: IdleRequestCallback, opts?: IdleRequestOptions) => number
  }
  if (typeof win.requestIdleCallback === 'function') {
    win.requestIdleCallback(() => clean(), { timeout: 1500 })
    return
  }
  setTimeout(clean, 0)
}

onMounted(() => {
  firstUrl.value = window.location.href
  runStorageCleanAsync()
  try {
    if (!localStorage.getItem('firstUrl')) {
      localStorage.setItem('firstUrl', firstUrl.value)
    }
  } catch {
    try {
      sessionStorage.setItem('firstUrl', firstUrl.value)
    } catch {}
  }

  const params = new URLSearchParams(window.location.search)
  const miniProgramVersionCode = params.get('versionCode')

  getMiniProgramVersion().then(({ data, error, statusCode }) => {
    if (error.value) {
      handleErr(data, error, statusCode)
    } else {
      const resData = data as unknown as Ref<CommonResponse<GetMiniProgramVersionResponse>>
      if (resData.value.code === CODE_OK) {
        console.log('resData', resData.value)
        versionCode.value = resData.value.data.auditVersionCode
        if (miniProgramVersionCode) {
          console
          auditMode.value = versionCode.value < miniProgramVersionCode ? 'true' : 'false'
        }
      } else {
        console.log('error:', resData.value.message)
      }
    }
  })
})

const isRouterShow = ref(true)
const reload = async () => {
  isRouterShow.value = false
  await nextTick()
  isRouterShow.value = true
}
provide('reload', reload)
</script>
<template>
  <el-config-provider size="large" :z-index="zIndex" :locale="locale">
    <NuxtLayout>
      <NuxtPage v-if="isRouterShow" />
    </NuxtLayout>
  </el-config-provider>
</template>
